import React, { useRef } from 'react';

import { Box, Paper, Table, TableContainer } from '@mui/material';

import GenericTableBody from './GenericTableBody';
import GenericTableHead from './GenericTableHead';
import GenericTablePagination from './GenericTablePagination';
import LoadingTableItems from './LoadingTableItems';
import NoItemsMessage from './NoItemsMessage';
import { classes } from './styles';
import TableArrows from './TableArrows';
import { handleScroll, tablePadding } from './tableHelper';

import type { SxProps } from '@mui/material';
import type { Dispatch } from 'react';

import type { ActionListItem } from './GenericTableActions';
import type { GenericTableAction } from './genericTableReducer';
import type {
  GenericTableData,
  TableActionItemAction,
  TablePaginationMetadata,
  TableSelectionAction,
  TPaginationState,
  TSortableColumn,
  TTableState
} from './types';

interface IGenericTableProps<T> {
  items: T[];
  isLoadingItems: boolean;
  dispatch: Dispatch<GenericTableAction<T>>;
  tableState: TTableState<T, TSortableColumn[]>;
  paginationState: TPaginationState<string>;
  headerStyle: (id: string, tableHovered: boolean) => SxProps;
  TableRows: React.FC<{
    column: TSortableColumn;
    item: T;
    tableHovered: boolean;
    dispatch?: Dispatch<TableActionItemAction<T> | TableSelectionAction<T>>;
  }>;
  TableActionList?: ActionListItem[];
  // Numbered `{ totalPages, totalItems }` (mode optional) or cursor metadata (`mode: 'cursor'`).
  pagination?: TablePaginationMetadata;
  allowMultipleSelection?: boolean;
  label: string;
  titleCellId?: string;
  userPreferenceSource?: string;
  customEmptyStateMessage?: string;
  emptyMessageOverride?: React.ReactNode;
  ExpandedContent?: React.FC<{ item: T }>;
  expandableCellIds?: string[];
  customRowStyles?: Record<string, SxProps>;
  /** Total width (px) of sticky right columns; when set, right arrow is placed before them instead of before the action column. e.g. 200 for Active (80) + Action (120). */
  stickyRightColumnsWidth?: number;
  /** When true, scroll shadow is not applied to the action column (assumed to be on the sticky column before it). */
  hasStickyColumnBeforeActions?: boolean;
}

const GenericTable = <T extends GenericTableData>({
  items,
  pagination,
  isLoadingItems,
  dispatch,
  tableState,
  paginationState,
  headerStyle,
  TableRows,
  TableActionList,
  allowMultipleSelection = true,
  label,
  titleCellId, // This is used to attach a ref to the title cell for the table arrows
  userPreferenceSource,
  customEmptyStateMessage,
  emptyMessageOverride,
  ExpandedContent,
  expandableCellIds,
  customRowStyles,
  stickyRightColumnsWidth,
  hasStickyColumnBeforeActions
}: IGenericTableProps<T>) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const titleCellRef = useRef<HTMLTableCellElement>(null);
  const actionCellRef = useRef<HTMLTableCellElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isHoveringRef = useRef(false);

  const renderPagination = () => {
    if (!pagination) return null;

    if (pagination.mode === 'cursor') {
      return (
        <GenericTablePagination
          dispatch={dispatch}
          metadata={pagination}
          mode="cursor"
          paginationState={paginationState}
          userPreferenceSource={userPreferenceSource}
        />
      );
    }

    return (
      <GenericTablePagination
        dispatch={dispatch}
        paginationState={paginationState}
        total={pagination.total}
        totalItems={pagination.totalItems}
        totalPages={pagination.totalPages}
        userPreferenceSource={userPreferenceSource}
      />
    );
  };

  return (
    <Box>
      <Box ref={parentRef} sx={classes.tableAndArrowsContainer}>
        {!isLoadingItems && (
          <TableArrows
            actionCellRef={actionCellRef}
            dispatch={dispatch}
            hoverTimeoutRef={hoverTimeoutRef}
            isHoveringRef={isHoveringRef}
            parentRef={parentRef}
            stickyRightColumnsWidth={stickyRightColumnsWidth}
            tableRef={tableRef}
            tableState={tableState}
            titleCellRef={titleCellRef}
          />
        )}
        <TableContainer
          ref={tableRef}
          component={Paper}
          onScroll={() => handleScroll(tableRef, dispatch)}
          sx={{ boxShadow: 'none', scrollBehavior: 'smooth' }}
        >
          <Table
            aria-label={`${label} table`}
            sx={{
              ...classes.tableWrapper,
              'th, td, tr, thead': {
                padding: `${tablePadding(tableState.density)} 10px`
              }
            }}
          >
            <GenericTableHead
              actionCellRef={actionCellRef}
              allowMultipleSelection={allowMultipleSelection}
              customRowStyles={customRowStyles}
              dispatch={dispatch}
              hasActionItems={!!TableActionList}
              hasStickyColumnBeforeActions={hasStickyColumnBeforeActions}
              headerStyle={headerStyle}
              hoverTimeoutRef={hoverTimeoutRef}
              isHoveringRef={isHoveringRef}
              items={items}
              paginationState={paginationState}
              tableRef={tableRef}
              tableState={tableState}
              titleCellId={titleCellId}
              titleCellRef={titleCellRef}
              userPreferenceSource={userPreferenceSource}
            />
            {!isLoadingItems && (
              <GenericTableBody
                actionCellRef={actionCellRef}
                allowMultipleSelection={allowMultipleSelection}
                customRowStyles={customRowStyles}
                dispatch={dispatch}
                expandableCellIds={expandableCellIds}
                ExpandedContent={ExpandedContent}
                hasStickyColumnBeforeActions={hasStickyColumnBeforeActions}
                hoverTimeoutRef={hoverTimeoutRef}
                isHoveringRef={isHoveringRef}
                items={items}
                TableActionList={TableActionList}
                tableRef={tableRef}
                TableRows={TableRows}
                tableState={tableState}
                titleCellRef={titleCellRef}
              />
            )}
          </Table>
          {isLoadingItems ? <LoadingTableItems numberOfRows={10} /> : null}
          {emptyMessageOverride != null ? (
            items.length === 0 && !isLoadingItems && emptyMessageOverride
          ) : (
            <NoItemsMessage
              customEmptyStateMessage={customEmptyStateMessage}
              label={label}
              showNoItemsMessage={items.length === 0 && !isLoadingItems}
            />
          )}
        </TableContainer>
      </Box>
      {renderPagination()}
    </Box>
  );
};

export default GenericTable;
